import { useCallback } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { PencilSimple, X } from "@phosphor-icons/react"
import { FormField } from "@/components/ui/form"
import { List, ListItem, ListText } from "@/components/ui/list"
import { cn } from "@/lib/utils"
import { DeMedicationDrawer } from "@/modules/patients/detail/medication/de/de-medication-drawer"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { generateMedicationDescription } from "@/modules/patients/detail/medication/lib/generate-medication-description"
import { isFreeTextMedicationType } from "@/modules/patients/detail/medication/lib/is-free-text-medication-type"
import { DeMedicationFragmentInlineList } from "@/modules/patients/detail/medication/types/medication-fragment-type"
import { DePrescriptionDrawerProps } from "@/modules/patients/detail/prescription/de/de-prescription-drawer"
import { MedicationSearch } from "@/modules/patients/detail/prescription/de/medication-search"
import {
  MAXIMUM_MEDICATIONS_PER_PRESCRIPTION,
  Prescription,
} from "@/modules/patients/detail/prescription/de/schema/prescription-schema"
import { useDePrescriptionStoreContext } from "@/modules/patients/detail/prescription/de/store/usePrescriptionStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { DeMedicationType } from "@/types/__generated__/union-types"

interface DePrescriptionContentProps extends Pick<
  DePrescriptionDrawerProps,
  "progressNoteId" | "prescriptionRef" | "id"
> {
  unSavedMedicationIds: string[]
}

const DePrescriptionContent = ({
  unSavedMedicationIds,
  prescriptionRef,
  id,
  progressNoteId,
}: DePrescriptionContentProps) => {
  const { t } = useTranslation()
  const { activeMedications, inactiveMedications } = useMedications<DeMedicationFragmentInlineList>()
  const removeUnSavedMedicationId = useDePrescriptionStoreContext((state) => state.removeUnSavedMedicationId)
  const setRemovedMedicationIds = useDePrescriptionStoreContext((state) => state.setRemovedMedicationIds)
  const openDrawerWithoutConfirmation = useDrawerStore((state) => state.openDrawerWithoutConfirmation)

  const form = useFormContext<Prescription>()
  const {
    formState: { errors },
    watch,
  } = form

  const watchMedicationIds = watch("medicationIds")

  const formatMedicationGroup = useCallback(
    (selectedIds: string[]) => {
      return [
        {
          title: t("patient.medication.active"),
          medications: activeMedications
            .map(({ name, id, type }) => ({
              label: name,
              value: id,
              type: type,
            }))
            .filter(({ value }) => !selectedIds.includes(value)),
        },
        {
          title: t("patient.medication.inactive"),
          medications: inactiveMedications
            .map(({ name, id, type }) => ({
              label: name,
              value: id,
              type: type,
            }))
            .filter(({ value }) => !selectedIds.includes(value)),
        },
      ]
    },
    [activeMedications, inactiveMedications, t],
  )

  const getMedication = (
    id: string,
  ): {
    name: string
    description: string
    type: DeMedicationType | null
    medication?: DeMedicationFragmentInlineList[number]
  } => {
    const activeMedication = [...activeMedications].find((medication) => medication.id === id)
    const inactiveMedication = [...inactiveMedications].find((medication) => medication.id === id)
    const medication = activeMedication ?? inactiveMedication

    if (!medication) return { name: "", description: "", type: null }

    const { name, type } = medication

    const activeDescription = activeMedication && generateMedicationDescription({ isActive: true, ...medication })
    const inactiveDescription = inactiveMedication && generateMedicationDescription({ isActive: false, ...medication })

    const description = activeDescription ?? inactiveDescription ?? ""

    return { name, description, type, medication }
  }

  const handleRemoveMedication = (id: string) => {
    if (unSavedMedicationIds.includes(id)) removeUnSavedMedicationId(id)
    else setRemovedMedicationIds(id)
  }

  const handleEditMedication = (medication?: DeMedicationFragmentInlineList[number]) => {
    if (!medication) return

    const existingPrescriptionMedication = {
      medication,
      prescriptionRef,
      id,
      progressNoteId,
    }
    openDrawerWithoutConfirmation(
      () => <DeMedicationDrawer prescriptionMedicationSearchResults={existingPrescriptionMedication} />,
      "MedicationDrawer",
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <label className="text-on-surface text-base font-medium">
        {t("patient.prescription.maxMedications", { max: MAXIMUM_MEDICATIONS_PER_PRESCRIPTION })}
      </label>
      <div className="flex flex-col gap-4">
        {watchMedicationIds.length < MAXIMUM_MEDICATIONS_PER_PRESCRIPTION && (
          <FormField
            name="medicationIds"
            render={({ field: { value, onChange } }) => {
              const formattedGroups = formatMedicationGroup(value)

              return (
                <MedicationSearch
                  error={errors?.medicationIds?.message ?? ""}
                  existingMedicationsGroups={formattedGroups}
                  onSelect={(selectedMedication) => {
                    onChange([...new Set([...value, selectedMedication.value])])
                  }}
                  prescriptionData={{ progressNoteId, id, prescriptionRef }}
                />
              )
            }}
          />
        )}
        <FormField
          name="medicationIds"
          render={({ field: { value, onChange } }) => {
            return (
              <div>
                <List asChild>
                  <>
                    {value.map((id: string) => {
                      const { name, description, type, medication } = getMedication(id)

                      return (
                        <ListItem key={id} asChild>
                          <div
                            data-testid="list-item"
                            className="hover:bg-surface-hovered relative min-h-12 items-center">
                            <div className="min-w-0">
                              <ListText className={cn("truncate", isFreeTextMedicationType(type) && "pr-1 italic")}>
                                {name}
                              </ListText>
                              <ListText variant="secondary" className="truncate">
                                {description}
                              </ListText>
                            </div>

                            <div className="ml-auto hidden group-hover:flex group-hover:gap-1">
                              <button
                                className="hover:bg-surface-hovered active:enabled:bg-surface-pressed flex h-9 w-9 items-center justify-center hover:rounded-full"
                                onClick={() => {
                                  const medications = new Set([...value])
                                  medications.delete(id)
                                  onChange([...medications])
                                  handleRemoveMedication(id)
                                }}>
                                <X className="text-on-surface-subdued" />
                              </button>
                              <button
                                className="hover:bg-surface-hovered active:enabled:bg-surface-pressed flex h-9 w-9 items-center justify-center hover:rounded-full disabled:cursor-not-allowed"
                                onClick={() => handleEditMedication(medication)}>
                                <PencilSimple className="text-on-surface-subdued" />
                              </button>
                            </div>
                          </div>
                        </ListItem>
                      )
                    })}
                  </>
                </List>
              </div>
            )
          }}
        />
      </div>
    </div>
  )
}

export { DePrescriptionContent }
